import React, { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

interface RevealProps {
  children: React.ReactNode;
  /** Delay in seconds before the reveal starts */
  delay?: number;
  /** Distance in px the element travels on its way in */
  y?: number;
  className?: string;
  /** Replay the animation every time the element re-enters the viewport */
  once?: boolean;
}

/**
 * Scroll-triggered reveal. Blurs + lifts content into place with a soft spring.
 * Falls back to a plain fade when the user prefers reduced motion.
 */
export const Reveal: React.FC<RevealProps> = ({
  children,
  delay = 0,
  y = 28,
  className = '',
  once = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once, margin: '-10% 0px -12% 0px' });
  const reduced = useReducedMotion();

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y, filter: 'blur(10px)', scale: 0.98 }}
      animate={
        inView
          ? reduced
            ? { opacity: 1 }
            : { opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }
          : undefined
      }
      transition={{
        duration: 0.9,
        delay,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

export default Reveal;
